import { create } from 'zustand';

/**
 * The child's own display preferences for this browser.
 *
 * **Only what the panel actually honours.** Every field below is read by a
 * screen; a preference nothing reads does not belong here. The Settings page
 * used to offer ten, of which one did anything: a language picker with no
 * translations behind it, a time-zone picker over date helpers deliberately
 * fixed to Asia/Kolkata, a List/Grid/Card view where only a list exists, a font
 * size against a type scale written in pixels, and a week-start-day over a
 * timetable whose columns are the *institute's* working days. Each one wrote to
 * `localStorage` and was then ignored, which is a setting that lies to the
 * person who changed it.
 *
 * **In the browser, not in a table.** These are per-device display choices —
 * the same child on the library computer and on their own phone may reasonably
 * want different answers — and there is no preference column anywhere in the
 * product. A new table would only make an ignored setting durable.
 *
 * The theme is deliberately absent: `hooks/useTheme` already owns it and
 * already persists it. Keeping a second copy here is how the tiles on this page
 * and the toggle in the top bar came to disagree.
 */
export interface StudentPreferences {
  /** How many rows a paged list on this panel shows at once. */
  itemsPerPage: number;
  /** Subject icons on the timetable. Off is a plainer, denser grid. */
  showSubjectIcons: boolean;
  /** Who set the work, took the lesson or put the notice up. */
  showTeacherName: boolean;
  /** Percentages beside the marks. Off leaves the raw scores. */
  showMarksPercentage: boolean;
}

interface PreferencesState extends StudentPreferences {
  set: <K extends keyof StudentPreferences>(key: K, value: StudentPreferences[K]) => void;
  reset: () => void;
}

const STORAGE_KEY = 'student_portal_settings';

export const ITEMS_PER_PAGE_OPTIONS = [5, 10, 20, 50] as const;

const DEFAULTS: StudentPreferences = {
  itemsPerPage: 10,
  showSubjectIcons: true,
  showTeacherName: true,
  showMarksPercentage: true,
};

/**
 * Whatever was stored, narrowed field by field.
 *
 * Never spread: the key held a larger object in an earlier build, and trusting
 * its shape would put a stale `language` string back into state. A value that
 * is not the right type falls back to the default rather than reaching a
 * component.
 */
function readStored(): StudentPreferences {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULTS;

    const stored = JSON.parse(raw) as Partial<Record<keyof StudentPreferences, unknown>>;
    const perPage = Number(stored.itemsPerPage);

    return {
      itemsPerPage: ITEMS_PER_PAGE_OPTIONS.includes(perPage as (typeof ITEMS_PER_PAGE_OPTIONS)[number])
        ? perPage
        : DEFAULTS.itemsPerPage,
      showSubjectIcons:
        typeof stored.showSubjectIcons === 'boolean'
          ? stored.showSubjectIcons
          : DEFAULTS.showSubjectIcons,
      showTeacherName:
        typeof stored.showTeacherName === 'boolean'
          ? stored.showTeacherName
          : DEFAULTS.showTeacherName,
      showMarksPercentage:
        typeof stored.showMarksPercentage === 'boolean'
          ? stored.showMarksPercentage
          : DEFAULTS.showMarksPercentage,
    };
  } catch {
    // Private browsing, cleared site data, storage disabled — the panel works
    // on the defaults rather than failing to draw.
    return DEFAULTS;
  }
}

function write(preferences: StudentPreferences): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
  } catch {
    // Nothing to do and nothing worth saying: the choice still applies to this
    // session, it simply will not survive a reload.
  }
}

export const useStudentPreferences = create<PreferencesState>((set, get) => ({
  ...readStored(),

  set: (key, value) => {
    set({ [key]: value } as Pick<StudentPreferences, typeof key>);

    const { itemsPerPage, showSubjectIcons, showTeacherName, showMarksPercentage } = get();
    write({ itemsPerPage, showSubjectIcons, showTeacherName, showMarksPercentage });
  },

  reset: () => {
    set(DEFAULTS);
    write(DEFAULTS);
  },
}));
